import React from 'react';
import { useRequestsCounts } from 'src/models';
import { useTranslation } from 'react-i18next';
import { If } from 'src/components/If';

import { useToast } from 'src/components/ui/use-toast';
import { generatePath, Link, useLocation, useNavigate } from 'react-router-dom';
import { appRoutes } from 'src/routes';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';
import { useRequestMutations } from 'src/features/requests/use-request-mutations';
import { Button } from 'src/components/ui/button/button';
import { ChevronLeft, Video } from 'lucide-react';
import { RequestStatusCell } from 'src/features/requests/request-table/request-status-cell';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from 'src/components/ui/breadcrumb';
import { PageHeading, PageHeadingTitle } from 'src/components/ui/page-heading';
import { useConfirm } from 'src/components/ConfirmModal';
import { ButtonLoader } from 'src/components/ui/loader';
import { WebViewAuthService } from 'src/services/Auth/WebView.auth';

const RevisionsBreadcrumbs: React.FC = () => {
  const { t } = useTranslation(['pages/shared', 'alerts']);
  const navigate = useNavigate();
  const location = useLocation();
  // useDecoratedRequestContext returns the request with concrete field
  // values; useCurrentRequest returns a wrapper that's always truthy even
  // before the query resolves, which made the `request && …` guard a no-op
  // and rendered a malformed status pill during the loading window.
  const request = useDecoratedRequestContext();

  return (
    <PageHeading>
      <Breadcrumb>
        <BreadcrumbList className={'tw-flex-nowrap'}>
          <BreadcrumbItem>
            <Button
              variant={'unset'}
              size={'iconXs'}
              onClick={() => {
                if (location.key !== 'default') {
                  history.back();
                } else {
                  navigate(appRoutes.requestsList);
                }
              }}
            >
              <ChevronLeft className={'tw-size-4'} />
            </Button>
            {!WebViewAuthService.isWebViewAuthEnabled() && (
              <BreadcrumbLink asChild>
                <Link to={appRoutes.dashboard}>
                  {t('common:dashboard', {
                    defaultValue: 'Dashboard',
                  })}
                </Link>
              </BreadcrumbLink>
            )}
          </BreadcrumbItem>
          {!WebViewAuthService.isWebViewAuthEnabled() && <BreadcrumbSeparator />}
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to={appRoutes.requestsList}>
                {t('common:requests', {
                  defaultValue: 'Requests',
                })}
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbPage>
            {t('common:revisions', {
              defaultValue: 'Revisions',
            })}
          </BreadcrumbPage>
          {/* Same cell the kanban + list views use, so the re-edit loader
              trails through Priority Queue → Editing → Ready for review on
              the detail page too (#2383). */}
          <RequestStatusCell request={request} />
        </BreadcrumbList>
      </Breadcrumb>
      <PageHeadingTitle className={'tw-flex-nowrap'}>
        <div>
          <Video className={'tw-size-6'} />
        </div>
        <span className={'tw-line-clamp-1'}>
          {request.title || t('common:revision', { defaultValue: 'Request revision' })}
        </span>
      </PageHeadingTitle>
    </PageHeading>
  );
};

const OutcomeRevisionsBreadcrumbs: React.FC<{ title: string; parentId: string }> = ({
  title,
  parentId,
}) => {
  const { t } = useTranslation('pages/shared');
  const navigate = useNavigate();
  const location = useLocation();
  const request = useDecoratedRequestContext();

  return (
    <PageHeading>
      <Breadcrumb>
        <BreadcrumbList className={'tw-flex-nowrap'}>
          <BreadcrumbItem>
            <Button
              variant={'unset'}
              size={'iconXs'}
              onClick={() => {
                if (location.key !== 'default') {
                  history.back();
                } else {
                  navigate(generatePath(appRoutes.request, { requestId: parentId }));
                }
              }}
            >
              <ChevronLeft className={'tw-size-4'} />
            </Button>
            {!WebViewAuthService.isWebViewAuthEnabled() && (
              <BreadcrumbLink asChild>
                <Link to={appRoutes.dashboard}>
                  {t('common:dashboard', {
                    defaultValue: 'Dashboard',
                  })}
                </Link>
              </BreadcrumbLink>
            )}
          </BreadcrumbItem>
          {!WebViewAuthService.isWebViewAuthEnabled() && <BreadcrumbSeparator />}
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to={appRoutes.requestsList}>
                {t('common:requests', {
                  defaultValue: 'Requests',
                })}
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to={generatePath(appRoutes.request, { requestId: parentId })}>
                {t('common:Request', {
                  defaultValue: 'Request',
                })}
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbPage>
            {t('common:revisions', {
              defaultValue: 'Revisions',
            })}
          </BreadcrumbPage>
          <RequestStatusCell request={request} />
        </BreadcrumbList>
      </Breadcrumb>
      <PageHeadingTitle className={'tw-flex-nowrap'}>
        <div>
          <Video className={'tw-size-6'} />
        </div>
        <span className={'tw-line-clamp-1'}>
          {title || t('common:revision', { defaultValue: 'Request revision' })}
        </span>
      </PageHeadingTitle>
    </PageHeading>
  );
};

const RevisionsContentTop: React.FC = () => {
  const { t } = useTranslation(['pages/shared', 'alerts']);
  const { confirm } = useConfirm();

  const request = useDecoratedRequestContext();
  const requestMutations = useRequestMutations();

  const { toast } = useToast();
  const { update } = useRequestsCounts(
    {},
    {
      enabled: false,
    },
  );

  const handleUndoReeditRequest = async () => {
    const confirmed = await confirm('', {
      modal_header: {
        content: t('Undo re-edit request')!,
      },
      modal_body: {
        content: t('Undoing will remove it from the re-editing queue.')!,
      },
    });

    if (!confirmed) {
      return;
    }

    await requestMutations.undoReeditRequest.mutateAsync(request.id, {
      onSuccess: () => {
        toast({
          description: t('Re-edit request has been undone.'),
        });
      },
    });
  };

  return (
    <div className={'tw-relative tw-z-[1] tw-mb-4 tw-flex tw-flex-wrap tw-gap-4 md:tw-mb-10'}>
      <div
        className={
          'tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4 md:tw-justify-start'
        }
      >
        <If when={request.flow === 'outcome'} else={<RevisionsBreadcrumbs />}>
          <OutcomeRevisionsBreadcrumbs title={request.title} parentId={request!.parent_id!} />
        </If>
      </div>

      <div className={'tw-ms-auto tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2'}>
        {/* Only show Undo while the re-edit is still in flight (no new
            revision yet after the timestamp) AND the editor hasn't picked
            it up yet. `hasUnresolvedReedit` captures the full in-flight
            window — including the queued status the row sits in after PR
            #360's delivered→queued auto-transition (#2383) — but it also
            stays true once the editor flips status to currently_editing.
            Excluding that phase explicitly keeps the customer from yanking
            work out from under an editor already mid-re-edit (#2459). Once
            a new revision is in, `hasUnresolvedReedit` flips to false and
            ReEditAndApproveButtons take over. */}
        {request.hasUnresolvedReedit && !request.isCurrentlyEditing && (
          <Button
            variant={'outline'}
            size={'responsive'}
            disabled={requestMutations.isAnyMutationLoading}
            onClick={handleUndoReeditRequest}
          >
            {t('Undo re-edit request')}
            <ButtonLoader loading={requestMutations.undoReeditRequest.isLoading} />
          </Button>
        )}
      </div>
    </div>
  );
};

export { RevisionsContentTop };
